import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "totalTimeDisplay", 
    "validationMessage", 
    "submitButton",
    "guardian1Email",
    "guardian2Email",
    "guardian1EmailError",
    "guardian2EmailError",
    "emailValidationError",
    "studentEmail",
    "studentEmailError"
  ]
  static values = {
    pupilId: Number,
    drivingSchoolId: Number,
    pupilToken: String,
    checkEmailUrl: String
  }

  connect() {
    // Delay to allow form to fully render
    setTimeout(() => {
      this.calculateAndValidate()
      this.validateEmails()
    }, 100)
    
    // Watch for changes to the form (when rows are added/removed)
    this.observeFormChanges()
  }
  
  getStudentEmailElement() {
    // Try to get from target first, otherwise find by selector
    if (this.hasStudentEmailTarget) {
      return this.studentEmailTarget
    }
    return this.element.querySelector('input[name="pupil[email]"]')
  }

  observeFormChanges() {
    // Use MutationObserver to watch for DOM changes (when rows are added/removed)
    const observer = new MutationObserver(() => {
      // Debounce validation calls
      clearTimeout(this.validationTimeout)
      this.validationTimeout = setTimeout(() => {
        this.calculateAndValidate()
      }, 100)
    })
    
    // Observe all add-to-form containers
    const containers = this.element.querySelectorAll('[data-add-to-form-target="container"]')
    containers.forEach(container => {
      observer.observe(container, {
        childList: true,
        subtree: true
      })
    })
    
    // Store observer so we can disconnect later if needed
    this.formObserver = observer
  }

  disconnect() {
    if (this.formObserver) {
      this.formObserver.disconnect()
    }
    if (this.validationTimeout) {
      clearTimeout(this.validationTimeout)
    }
  }

  calculateAndValidate() {
    // Calculate package time available (separate for BTW and observation)
    const packageTimes = this.calculatePackageTimeAvailable()
    
    // Calculate lesson time required (separate for BTW and observation)
    const lessonTimes = this.calculateLessonTimeRequired()
    
    // Validate BTW and observation time separately
    this.validatePackageTime(packageTimes, lessonTimes)
  }

  /**
   * Calculate total package time available (separate for BTW and observation)
   * @returns {Object} { btwMinutes: number, observationMinutes: number }
   */
  calculatePackageTimeAvailable() {
    let btwMinutes = 0
    let observationMinutes = 0
    
    // Find the purchases container by looking for any input with "purchases" in the name
    const containers = this.element.querySelectorAll('[data-add-to-form-target="container"]')
    
    containers.forEach(container => {
      // Check if this container has purchase inputs (any input with "purchases" in name)
      const testInput = container.querySelector('input[name*="purchases"]')
      if (!testInput) return
      
      // This is the purchases container - process all rows
      container.querySelectorAll('[data-add-to-form-target="row"]').forEach(row => {
        // Get all inputs in this row and check their names
        row.querySelectorAll('input').forEach(input => {
          const inputName = input.getAttribute('name') || ''
          
          // Check for BTW time (duration_minutes field)
          if (inputName.includes('purchases') && inputName.includes('[duration_minutes]')) {
            if (input.value) {
              const value = parseInt(input.value) || 0
              btwMinutes += value
            }
          }
          
          // Check for observation time already stored in minutes.
          if (inputName.includes('purchases') && inputName.includes('[observations_in_minutes]')) {
            if (input.value) {
              const value = parseInt(input.value) || 0
              observationMinutes += value
            }
          }
        })
      })
    })
    
    return { btwMinutes, observationMinutes }
  }

  /**
   * Calculate total lesson time required (separate for BTW and observation)
   * Only counts lessons where use_time checkbox is checked
   * @returns {Object} { btwMinutes: number, observationMinutes: number }
   */
  calculateLessonTimeRequired() {
    let btwMinutes = 0
    let observationMinutes = 0
    
    // Find all containers that have lesson duration inputs
    const containers = this.element.querySelectorAll('[data-add-to-form-target="container"]')
    
    containers.forEach(container => {
      // Get a sample input to determine if this is BTW or observation lessons
      const sampleInput = container.querySelector('input[name*="[duration_in_minutes]"]')
      if (!sampleInput) return
      
      const inputName = sampleInput.getAttribute('name') || ''
      const isObservationLessons = inputName.includes('observation_lessons')
      
      // Calculate lesson time (only where use_time is checked)
      container.querySelectorAll('[data-add-to-form-target="row"]').forEach(row => {
        const durationInput = row.querySelector('input[name*="[duration_in_minutes]"]')
        const useTimeCheckbox = row.querySelector('input[type="checkbox"][name*="[use_time]"]')
        
        // Only count if use_time is checked (checkbox exists and is checked)
        const useTime = useTimeCheckbox && useTimeCheckbox.checked
        
        if (useTime && durationInput && durationInput.value) {
          const value = parseInt(durationInput.value) || 0
          
          if (isObservationLessons) {
            observationMinutes += value
          } else {
            // This is BTW lessons (param name is "lessons")
            btwMinutes += value
          }
        }
      })
    })
    
    return { btwMinutes, observationMinutes }
  }

  /**
   * Validate package time against lesson time requirements
   * Shows validation errors and enables/disables submit button
   */
  validatePackageTime(packageTimes, lessonTimes) {
    const btwBalance = packageTimes.btwMinutes - lessonTimes.btwMinutes
    const observationBalance = packageTimes.observationMinutes - lessonTimes.observationMinutes
    
    const btwValid = btwBalance >= 0
    const observationValid = observationBalance >= 0
    
    // Check email validation
    const emailErrors = this.validateEmails()
    
    // Build validation messages
    const errors = []
    
    if (!btwValid) {
      const deficitMinutes = Math.abs(btwBalance)
      const deficitHours = Math.floor(deficitMinutes / 60)
      const deficitMins = deficitMinutes % 60
      errors.push(`BTW Lessons require ${deficitHours}h ${deficitMins}m more than available package time.`)
    }
    
    if (!observationValid) {
      const deficitMinutes = Math.abs(observationBalance)
      const deficitHours = Math.floor(deficitMinutes / 60)
      const deficitMins = deficitMinutes % 60
      errors.push(`Observation Lessons require ${deficitHours}h ${deficitMins}m more than available observation time.`)
    }
    
    // Display validation messages
    if (this.hasValidationMessageTarget) {
      if (errors.length > 0) {
        this.validationMessageTarget.innerHTML = 
          `<div class="m0">
            <p class="mb1"><strong>⚠️ Package Time Validation Errors:</strong></p>
            <ul class="m0 pl3">
              ${errors.map(error => `<li>${error}</li>`).join('')}
            </ul>
            <p class="mt2 mb0">Please add more package time or remove some lessons.</p>
          </div>`
        this.validationMessageTarget.className = 'Panel Panel--alert p2 my2'
        this.validationMessageTarget.hidden = false
      } else if (emailErrors.length > 0) {
        // Hide time validation, email errors shown separately
        this.validationMessageTarget.hidden = true
      } else {
        // All validations pass
        this.validationMessageTarget.hidden = true
        this.hideEmailValidationError()
      }
    }
    
    // Handle submit button state
    const hasErrors = errors.length > 0 || emailErrors.length > 0
    
    // Find all submit buttons with the target attribute
    const submitButtons = this.element.querySelectorAll('[data-import-review-validator-target="submitButton"]')
    submitButtons.forEach(button => {
      if (hasErrors) {
        button.disabled = true
        button.classList.add('is-disabled')
      } else {
        button.disabled = false
        button.classList.remove('is-disabled')
      }
    })
  }

  validateEmails() {
    const errors = []
    
    // Reset error displays first (but don't clear student email error as it might be from availability check)
    this.hideGuardian1Error()
    this.hideGuardian2Error()
    // Don't hide email validation error here - it might contain availability error
    
    // Get email values (normalize to lowercase for comparison)
    const studentEmailElement = this.getStudentEmailElement()
    const studentEmail = (studentEmailElement?.value || '').trim().toLowerCase()
    const guardian1Email = this.hasGuardian1EmailTarget ? (this.guardian1EmailTarget?.value?.trim().toLowerCase() || '') : ''
    const guardian2Email = this.hasGuardian2EmailTarget ? (this.guardian2EmailTarget?.value?.trim().toLowerCase() || '') : ''
    
    // Email format regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    
    // Track what types of errors we have
    let hasStudentFormatError = false
    let hasGuardian1FormatError = false
    let hasGuardian2FormatError = false
    let hasConflictError = false
    let conflictMessage = ''
    
    // Validate student email format if present
    if (studentEmail && !emailRegex.test(studentEmail)) {
      hasStudentFormatError = true
      this.showStudentEmailError('Please enter a valid email address')
      errors.push('student_format')
    }
    
    // Validate guardian 1 email format
    if (guardian1Email && !emailRegex.test(guardian1Email)) {
      hasGuardian1FormatError = true
      errors.push('guardian1_format')
    }
    
    // Validate guardian 2 email format
    if (guardian2Email && !emailRegex.test(guardian2Email)) {
      hasGuardian2FormatError = true
      errors.push('guardian2_format')
    }
    
    // Only check conflicts if emails are valid format
    if (!hasStudentFormatError && !hasGuardian1FormatError && studentEmail && guardian1Email) {
      if (studentEmail === guardian1Email) {
        hasConflictError = true
        conflictMessage = 'Student email cannot be the same as guardian email'
        errors.push('student_guardian1_conflict')
      }
    }
    
    if (!hasStudentFormatError && !hasGuardian2FormatError && studentEmail && guardian2Email) {
      if (studentEmail === guardian2Email) {
        hasConflictError = true
        conflictMessage = 'Student email cannot be the same as second guardian email'
        errors.push('student_guardian2_conflict')
      }
    }
    
    if (!hasGuardian1FormatError && !hasGuardian2FormatError && guardian1Email && guardian2Email) {
      if (guardian1Email === guardian2Email) {
        hasConflictError = true
        conflictMessage = 'Guardian emails must be different'
        errors.push('guardian1_guardian2_conflict')
      }
    }
    
    // Display errors
    if (hasGuardian1FormatError) {
      this.showGuardian1Error('Please enter a valid email address')
    } else if (hasConflictError && guardian1Email && studentEmail && studentEmail === guardian1Email) {
      this.showGuardian1Error('Cannot be the same as student email')
    } else if (hasConflictError && guardian1Email && guardian2Email && guardian1Email === guardian2Email) {
      // This error is shown on guardian2, so we don't show it here
    }
    
    if (hasGuardian2FormatError) {
      this.showGuardian2Error('Please enter a valid email address')
    } else if (hasConflictError && guardian2Email && studentEmail && studentEmail === guardian2Email) {
      this.showGuardian2Error('Cannot be the same as student email')
    } else if (hasConflictError && guardian2Email && guardian1Email && guardian1Email === guardian2Email) {
      this.showGuardian2Error('Cannot be the same as first guardian email')
    }
    
    // Show global error for student format errors or conflicts
    if (hasStudentFormatError) {
      this.showEmailValidationError('Please enter a valid student email address')
    } else if (hasConflictError && conflictMessage) {
      this.showEmailValidationError(conflictMessage)
    } else if (errors.length > 0 && !hasGuardian1FormatError && !hasGuardian2FormatError) {
      // If we have errors but no format errors shown inline, show a generic message
      this.showEmailValidationError('Please fix email validation errors')
    }
    
    return errors
  }

  showGuardian1Error(message) {
    if (this.hasGuardian1EmailErrorTarget) {
      this.guardian1EmailErrorTarget.textContent = message
      this.guardian1EmailErrorTarget.hidden = false
    }
  }

  hideGuardian1Error() {
    if (this.hasGuardian1EmailErrorTarget) {
      this.guardian1EmailErrorTarget.hidden = true
    }
  }

  showGuardian2Error(message) {
    if (this.hasGuardian2EmailErrorTarget) {
      this.guardian2EmailErrorTarget.textContent = message
      this.guardian2EmailErrorTarget.hidden = false
    }
  }

  hideGuardian2Error() {
    if (this.hasGuardian2EmailErrorTarget) {
      this.guardian2EmailErrorTarget.hidden = true
    }
  }

  showEmailValidationError(message) {
    if (this.hasEmailValidationErrorTarget) {
      this.emailValidationErrorTarget.textContent = message
      this.emailValidationErrorTarget.hidden = false
    }
  }

  hideEmailValidationError() {
    if (this.hasEmailValidationErrorTarget) {
      this.emailValidationErrorTarget.hidden = true
    }
  }

  showStudentEmailError(message) {
    if (this.hasStudentEmailErrorTarget) {
      this.studentEmailErrorTarget.textContent = message
      this.studentEmailErrorTarget.hidden = false
    } else if (this.hasEmailValidationErrorTarget) {
      // Fallback to global error if no inline error target
      this.showEmailValidationError(message)
    }
  }

  hideStudentEmailError() {
    if (this.hasStudentEmailErrorTarget) {
      this.studentEmailErrorTarget.hidden = true
    }
  }

  async checkEmailAvailability(event) {
    const emailInput = event.target
    const email = emailInput.value?.trim()
    
    // Determine email type based on which input triggered the event
    // Check by name attribute first as it's more reliable
    const inputName = emailInput.getAttribute('name') || ''
    let emailType = 'student'
    let errorTarget = null
    let availabilityErrorFlag = 'studentEmailAvailabilityError'
    
    // Determine email type by name attribute
    if (inputName.includes('[emergency_contact_email]') && !inputName.includes('second')) {
      emailType = 'guardian1'
      errorTarget = this.hasGuardian1EmailErrorTarget ? this.guardian1EmailErrorTarget : null
      availabilityErrorFlag = 'guardian1EmailAvailabilityError'
    } else if (inputName.includes('[second_emergency_contact_email]')) {
      emailType = 'guardian2'
      errorTarget = this.hasGuardian2EmailErrorTarget ? this.guardian2EmailErrorTarget : null
      availabilityErrorFlag = 'guardian2EmailAvailabilityError'
    } else if (inputName.includes('[email]') && !inputName.includes('emergency')) {
      emailType = 'student'
      errorTarget = this.hasStudentEmailErrorTarget ? this.studentEmailErrorTarget : null
      availabilityErrorFlag = 'studentEmailAvailabilityError'
    }
    
    // First run basic validation
    this.validateEmails()
    
    // Only check availability if email format is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email || !emailRegex.test(email)) {
      return // Format validation will handle this
    }
    
    // Check if pupilTokenValue is available
    if (!this.pupilTokenValue) {
      return
    }
    
    // Debounce the API call
    clearTimeout(this.emailCheckTimeout)
    this.emailCheckTimeout = setTimeout(async () => {
      try {
        const baseTemplate = this.checkEmailUrlValue || '/simple-importer/pupils/:pupil_token/import-review/check-email'
        const baseUrl = baseTemplate.replace(':pupil_token', encodeURIComponent(this.pupilTokenValue))
        const url = `${baseUrl}?email=${encodeURIComponent(email)}&email_type=${emailType}`
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
            'Accept': 'application/json'
          }
        })
        
        if (!response.ok) {
          return
        }
        
        const data = await response.json()
        
        if (!data.available) {
          // Show error in appropriate location
          const errorMessage = data.message || 'This email address is already in use'
          if (errorTarget) {
            errorTarget.textContent = errorMessage
            // Ensure correct styling - remove orange, add red
            errorTarget.classList.remove('u-textOrange')
            errorTarget.classList.add('u-textRed')
            errorTarget.hidden = false
          }
          this.showEmailValidationError(errorMessage)
          this[availabilityErrorFlag] = true
          
          if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true
            this.submitButtonTarget.classList.add('is-disabled')
          }
        } else {
          // Email is available - clear any errors
          if (errorTarget) {
            errorTarget.hidden = true
          }
          this[availabilityErrorFlag] = false
          
          // Clear global error if no other errors exist
          this.validateEmails()
          
          // Re-check time balance
          this.calculateAndValidate()
        }
      } catch (error) {
        // Don't block submission on check failure
      }
    }, 500) // 500ms debounce
  }

  // Called when lessons are added/removed/changed
  lessonChanged() {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      this.calculateAndValidate()
    }, 50)
  }

  // Called when purchases are added/removed/changed
  purchaseChanged() {
    // Use setTimeout to ensure DOM is updated
    setTimeout(() => {
      this.calculateAndValidate()
    }, 50)
  }
}
